import { AfterViewChecked, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Experience } from '../../../services/interfaces/experiences';
import Map from 'ol/Map';
import View from 'ol/View';
import { Cluster, OSM } from 'ol/source';
import TileLayer from 'ol/layer/Tile';
import VectorSource from 'ol/source/Vector';
import Feature, { FeatureLike } from 'ol/Feature';
import {
  Circle as CircleStyle,
  Fill,
  RegularShape,
  Stroke,
  Style,
  Text,
} from 'ol/style.js';
import Point from 'ol/geom/Point';
import { fromLonLat, toLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import { createEmpty, getBottomRight, getTopLeft, getCenter, extend } from 'ol/extent';
import { MapBrowserEvent, Overlay } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';

@Component({
  selector: 'app-experiences-map',
  templateUrl: './experiences-map.component.html',
  styleUrl: './experiences-map.component.scss'
})
export class ExperiencesMapComponent implements OnInit, AfterViewChecked, OnChanges, OnDestroy {

  @Input() cards: Experience.Short[] = [];
  @Output() clickOnExperience = new EventEmitter<Experience.Short>();
  @Output() moveMapEvent = new EventEmitter<any>();
  public map!: Map;
  markers: any;

  currentSelectedExperiences: Experience.Short[] | any[] | undefined;
  vectorSource?: VectorSource;
  vectorSourceCurrentPosition?: VectorSource;
  private moveDebounceTimer: any;

  ngOnInit(): void {
    this.map = new Map({
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      target: 'map',
      view: new View({
        center: fromLonLat([9.206060080981757, 45.44124646335923]),
        zoom: 10, maxZoom: 16,
      }),
    });

    this.prepareVectorLayer();
    this.attachMapEvents();
    this.setCurrentPosition();
  }

  ngAfterViewChecked(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cards']) {
      //CLEAN OLD MARKERS
      if(this.cards.length > 0){
        this.vectorSource?.clear(true);
      }
      this.cards.forEach(card => this.addExperienceMarker(card));
    }
  }

  addExperienceMarker(card: Experience.Short): any {
    if (card && card.location) {
      const price = card.price_cents! / 100 || 0;
      const marker = new Feature({
        geometry: new Point(fromLonLat([card.location.coordinates[0], card.location.coordinates[1]])),
        price: price,
        id: card.id,
        exp: card
      });

      this.vectorSource!.addFeature(marker);
    }
  }

  onClick(exp: Experience.Short) {
    if (exp && exp.id) {
      this.clickOnExperience.emit(exp);
    }
  }

  onChangeMap(event: any) {
    const map = event.map;
    const extent = map.getView().calculateExtent(map.getSize());
    const getBottomRightPoint = toLonLat(getBottomRight(extent));
    const getTopLeftPoint = toLonLat(getTopLeft(extent));
    this.moveMapEvent.emit({ type: "envelope", coordinates: [[getTopLeftPoint[1], getTopLeftPoint[0]], [getBottomRightPoint[1], getBottomRightPoint[0]]] });
  }

  attachMapEvents() {
    // POP UP
    /**
     * Elements that make up the popup.
     */
    const container = document.getElementById('popup');

    /**
     * Create an overlay to anchor the popup to the map.
     */
    const overlay = new Overlay({
      element: container!,
      autoPan: {
        animation: {
          duration: 250,
        },
      },
    });

    this.map.addOverlay(overlay);

    //
    this.map.on('click', (event) => {
      this.checkIfClickOnVector(event, overlay);
    });

    this.map.on('moveend', (event) => {
      // Debounce map movement to avoid too many API calls
      if (this.moveDebounceTimer) {
        clearTimeout(this.moveDebounceTimer);
      }
      this.moveDebounceTimer = setTimeout(() => {
        this.onChangeMap(event);
      }, 500); // Wait 500ms after map stops moving before triggering search
    });
  }

  checkIfClickOnVector(event: MapBrowserEvent<any>, overlay: Overlay) {
    const coordinate = event.coordinate;
    const map = this.map;
    const feature = this.map.forEachFeatureAtPixel(event.pixel, (feature) => feature);
    if (feature) {
      const cluster: Feature[] = feature.get('features');
      if (cluster.length > 1) {
        // check if the actual zoom is the maxiumum and if not zoom to that position with max zoom
        const maxZoom = map.getView().getMaxZoom()!;
        const currentZoom = map.getView().getZoom()!;
        if (currentZoom < maxZoom) {
          // Calculate the extent of the cluster
          let extent = createEmpty();
          cluster.forEach(feature => {
            let featureExtent = feature!.getGeometry()!.getExtent();
            extend(extent, featureExtent);
          });

          // Calculate zoom level based on extent
          const resolution = map.getView().getResolutionForExtent(extent);
          const zoom = resolution ? map.getView().getZoomForResolution(resolution)! : map.getView().getMaxZoom()!;

          // Zoom to the calculated zoom level and center
          map.getView().setCenter(getCenter(extent));
          map.getView().setZoom(zoom < map.getView().getMaxZoom() ? zoom - 1 : map.getView().getMaxZoom());
        } else {
          // MAX ZOOM REACHED
          // Handle click on cluster
          overlay.setPosition(coordinate);
          // SET SELECTED EXPERIENCE
          var dd = cluster.map((feature: { get: (arg0: string) => string; }) => this.cards.find(card => card.id === feature.get('id')));
          this.currentSelectedExperiences = dd;
        }

      } else {
        // Handle click on individual marker
        const originalFeature = cluster[0];
        const exp = originalFeature.get('exp');
        //const exp = this.cards.find(card => card.id === originalFeature.get('id'));
        if (exp) {
          // Show popup or emit event
          overlay.setPosition(coordinate);
          // SET SELECTED EXPERIENCE
          this.currentSelectedExperiences = [exp];
        }
      }
    } else {
      overlay.setPosition(undefined);
    }
  }

  prepareVectorLayer() {
    this.vectorSource = new VectorSource({
      format: new GeoJSON(),
    });

    this.vectorSourceCurrentPosition = new VectorSource({
      format: new GeoJSON(),
    });

    const cPositionStyle = (feature: FeatureLike) => {
      let style = new Style({
        image: new CircleStyle({
          radius: 7,
          fill: new Fill({ color: 'rgba(8, 141, 250, 0.95)' }), // divinea color for cluster
          stroke: new Stroke({ color: '#00f', width: 1 }), // Black border
        }),
        text: new Text({
          text: "",
          font: '15px Mulish',
          fill: new Fill({ color: '#fff' }), // White color for text
        })
      });

      return style;
    };

    const clusterSource = new Cluster({
      distance: 25,
      source: this.vectorSource,
    });

    const clusterStyle = (feature: FeatureLike) => {
      const size = feature.get('features').length;
      let style;

      if (size > 1) {
        style = new Style({
          image: new CircleStyle({
            radius: 16,
            fill: new Fill({ color: 'rgba(186, 50, 79, 0.8)' }), // divinea color for cluster
          }),
          text: new Text({
            text: size.toString(),
            font: '15px Mulish',
            fill: new Fill({ color: '#fff' }), // White color for text
          }),
        });
      } else {
        const originalFeature = feature.get('features')[0];
        const price = originalFeature.get('price') || 0;
        style = new Style({
          image: new RegularShape({
            fill: new Fill({ color: 'rgba(255, 255, 255, 0.9)' }), // White color for the rectangle
            stroke: new Stroke({ color: '#000', width: 1 }), // Black border
            points: 4, // 4 points to form a rectangle
            radius: 25,
            scale: [1, 0.5],
            angle: Math.PI / 4, // No rotation
          }),
          text: new Text({
            text: price.toString() + '€', // Display the price as text
            font: '14px Mulish',
            fill: new Fill({ color: '#000' })
          }),
        });
      }

      return style;
    };

    this.markers = new VectorLayer({
      source: clusterSource,
      style: clusterStyle
    });

    var layy = new VectorLayer({
      source: this.vectorSourceCurrentPosition,
      style: cPositionStyle
    });

    this.map.addLayer(this.markers);
    this.map.addLayer(layy);
  }

  setCurrentPosition() {
    const that = this;
    const options = {
      enableHighAccuracy: false,
      timeout: 25000, // 25sec
      maximumAge: 8640000 // 1d
    };

    function intfromLonLat(x: any) {
      return fromLonLat(x);
    }

    function fnSuccess(event: any) {
      const longitude = event.coords.longitude;
      const latitude = event.coords.latitude;

      // Create a new position from the received coordinates

      var position = intfromLonLat([longitude, latitude]);
      const marker = new Feature({
        geometry: new Point(fromLonLat([longitude, latitude]))
      });
      that.vectorSourceCurrentPosition!.clear();
      that.vectorSourceCurrentPosition!.addFeature(marker);

      // Set the map view to the new position
      that.map.getView().setCenter(position);
    }

    function fnError(error: any) {
      // Handle error if needed
      console.error('Error getting geolocation:', error);
    }

    navigator.geolocation.getCurrentPosition(fnSuccess, fnError, options);
  }

  ngOnDestroy(): void {
    // Clean up debounce timer
    if (this.moveDebounceTimer) {
      clearTimeout(this.moveDebounceTimer);
    }
  }
}


